import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import { PageHeader } from "@/components/shell/PageHeader";
import {
  MessageSquare,
  Plus,
  Globe,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Send,
  HelpCircle,
  Link2,
  Shield,
  Lock,
  BarChart3,
  Settings,
  Users
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Support = () => {
  const { token } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [expandedTicket, setExpandedTicket] = useState(null);
  
  // New ticket form
  const [ticketType, setTicketType] = useState("issue");
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketMessage, setTicketMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  // Reply state
  const [replyMessage, setReplyMessage] = useState("");
  const [replying, setReplying] = useState(false);

  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      icon: Link2,
      q: "How do I create a redirect link?",
      a: "Go to 'Redirector Links' in the sidebar. Click 'Create Campaign', enter your destination URL, select a landing page template, and choose an alias path. The system will generate a Sender Link and Test Link for you."
    },
    {
      icon: Shield,
      q: "What does link rotation do?",
      a: "Link rotation distributes traffic across multiple domains to prevent domain burning. Enable it under Settings > Rotation to automatically cycle through your assigned domains during campaigns for better inbox delivery."
    },
    {
      icon: Globe,
      q: "How do I restrict access by country?",
      a: "Go to 'Geo Access' in the sidebar. You can whitelist specific countries (only those countries can access your links) or blacklist countries (those countries will be blocked). Changes sync to all your redirect servers automatically."
    },
    {
      icon: Lock,
      q: "What are the security features?",
      a: "Under Settings > Advanced Security, you can configure bot detection sensitivity, block datacenter IPs, enable device fingerprinting, and set up IP whitelisting/blacklisting. The system automatically blocks known bots, scanners, and suspicious traffic."
    },
    {
      icon: BarChart3,
      q: "How do I track my campaign performance?",
      a: "The Dashboard shows real-time stats including total visits, human vs bot traffic, and country breakdown. For deeper insights, visit the 'Insights' page which shows conversion funnels, geo heatmaps, and threat intelligence data."
    },
    {
      icon: Settings,
      q: "What do the different visitor statuses mean?",
      a: "REDIRECTED = Visitor passed all checks and was sent to your destination. BLOCKED = Visitor was blocked by security rules (bot, blacklisted IP/country, datacenter). SUSPICIOUS = Visitor triggered some security flags but wasn't fully blocked."
    },
    {
      icon: Users,
      q: "How does billing work?",
      a: "Go to 'Billing' to view your subscription status, remaining balance, and transaction history. You can submit payments and they will be verified by the admin. Your access is active as long as your subscription is current."
    },
    {
      icon: HelpCircle,
      q: "I need help — how do I contact support?",
      a: "Use the 'New Ticket' button above to create a support ticket. Describe your issue with as much detail as possible and the admin team will respond. You can track all your tickets and responses here."
    },
  ];

  const fetchTickets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data.tickets);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchTickets();
    const interval = setInterval(fetchTickets, 15000);
    return () => clearInterval(interval);
  }, [fetchTickets]);

  const submitTicket = async () => {
    if (!ticketSubject || !ticketMessage) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/support/tickets`, {
        type: ticketType,
        subject: ticketSubject,
        message: ticketMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicketType("issue");
      setTicketSubject("");
      setTicketMessage("");
      setShowNewTicket(false);
      fetchTickets();
    } catch (error) {
      console.error("Error submitting ticket:", error);
    }
    setSubmitting(false);
  };

  const replyToTicket = async (ticketId) => {
    if (!replyMessage.trim()) return;
    setReplying(true);
    try {
      await axios.post(`${API}/support/tickets/${ticketId}/reply`, {
        message: replyMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReplyMessage("");
      fetchTickets();
    } catch (error) {
      console.error("Error replying to ticket:", error);
      alert(error.response?.data?.detail || "Error sending reply");
    }
    setReplying(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "open":
        return <span className="badge-warning flex items-center gap-1"><Clock className="w-3 h-3" /> Open</span>;
      case "in_progress":
        return <span className="badge-neutral flex items-center gap-1"><AlertCircle className="w-3 h-3" /> In Progress</span>;
      case "resolved":
        return <span className="badge-success flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Resolved</span>;
      case "closed":
        return <span className="badge-danger flex items-center gap-1"><XCircle className="w-3 h-3" /> Closed</span>;
      default:
        return <span className="badge-neutral">{status}</span>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "domain_request":
        return <span className="text-xs px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">Domain Request</span>;
      case "issue":
        return <span className="text-xs px-2 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20">Issue</span>;
      case "question":
        return <span className="text-xs px-2 py-0.5 rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">Question</span>;
      default:
        return <span className="badge-neutral">{type}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        testId="support"
        eyebrow="Help desk"
        icon={HelpCircle}
        title="Support"
        subtitle="Open a ticket, request a new domain or follow up on an existing conversation."
        actions={<button onClick={() => setShowNewTicket(!showNewTicket)} className="btn-primary flex items-center gap-2" data-testid="new-ticket-btn"><Plus className="w-4 h-4" />New Request</button>}
      />

      {/* New Ticket Form */}
      {showNewTicket && (
        <div className="card-dark p-6 animate-fadeIn" data-testid="new-ticket-form">
          <h3 className="text-lg font-semibold text-white mb-4">Create Support Request</h3>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 block mb-2">Request Type</label>
              <div className="flex gap-3">
                {[
                  { value: "issue", label: "Report Issue", icon: AlertCircle },
                  { value: "question", label: "General Question", icon: MessageSquare }
                ].map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setTicketType(type.value)}
                    className={`flex-1 p-3 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                      ticketType === type.value
                        ? "bg-cyan-500/10 border-cyan-500/50 text-cyan-400"
                        : "bg-slate-800/50 border-slate-700/50 text-slate-400 hover:border-slate-600"
                    }`}
                    data-testid={`type-${type.value}`}
                  >
                    <type.icon className="w-4 h-4" />
                    <span className="text-sm">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm text-slate-400 block mb-2">Subject</label>
              <input
                type="text"
                placeholder={ticketType === "domain_request" ? "e.g., Need domain for US traffic" : "Brief description of your request"}
                value={ticketSubject}
                onChange={(e) => setTicketSubject(e.target.value)}
                className="input-dark w-full"
                data-testid="ticket-subject"
              />
            </div>

            <div>
              <label className="text-sm text-slate-400 block mb-2">Message</label>
              <textarea
                placeholder={ticketType === "domain_request" 
                  ? "Explain why you need a new domain. Include: target region, current domain issues, expected traffic volume..."
                  : "Provide details about your request..."
                }
                value={ticketMessage}
                onChange={(e) => setTicketMessage(e.target.value)}
                className="input-dark w-full h-32 resize-none"
                data-testid="ticket-message"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowNewTicket(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={submitTicket}
                disabled={!ticketSubject || !ticketMessage || submitting}
                className="btn-primary flex items-center gap-2"
                data-testid="submit-ticket-btn"
              >
                <Send className="w-4 h-4" />
                {submitting ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={() => { setShowNewTicket(true); setTicketType("issue"); }}
          className="card-dark p-4 text-left hover:border-red-500/30 transition-all group"
        >
          <AlertCircle className="w-8 h-8 text-red-400 mb-2 group-hover:scale-110 transition-transform" />
          <p className="font-medium text-white">Report an Issue</p>
          <p className="text-xs text-slate-500 mt-1">Domain not working or other problems</p>
        </button>
        <button
          onClick={() => { setShowNewTicket(true); setTicketType("question"); }}
          className="card-dark p-4 text-left hover:border-purple-500/30 transition-all group"
        >
          <MessageSquare className="w-8 h-8 text-purple-400 mb-2 group-hover:scale-110 transition-transform" />
          <p className="font-medium text-white">Ask a Question</p>
          <p className="text-xs text-slate-500 mt-1">General inquiries and help</p>
        </button>
      </div>

      {/* Telegram Support Contact */}
      <div className="card-dark p-5 border border-cyan-500/20 bg-gradient-to-r from-cyan-500/5 to-transparent">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-cyan-500/15 rounded-full flex items-center justify-center flex-shrink-0">
            <Send className="w-6 h-6 text-cyan-400" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-white">Need instant help?</p>
            <p className="text-sm text-slate-400 mt-0.5">Contact support directly on Telegram for faster response</p>
          </div>
          <a
            href="https://t.me/X2ARES"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-5 py-2.5 bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/25 transition-colors font-medium text-sm"
            data-testid="telegram-support-link"
          >
            <Send className="w-4 h-4" />
            @X2ARES
          </a>
        </div>
      </div>

      {/* Tickets List */}
      <div className="card-dark p-6" data-testid="tickets-list">
        <h3 className="text-lg font-semibold text-white mb-4">Your Requests</h3>
        
        <div className="space-y-3">
          {tickets.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No support requests yet</p>
              <p className="text-slate-500 text-sm mt-1">Click "New Request" to get started</p>
            </div>
          ) : (
            tickets.map((ticket) => (
              <div
                key={ticket.id}
                className="bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-slate-800/80 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-left">
                      <p className="font-medium text-white">{ticket.subject}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {getTypeBadge(ticket.type)}
                        <span className="text-xs text-slate-500">
                          {new Date(ticket.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(ticket.status)}
                    {expandedTicket === ticket.id ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </button>
                
                {expandedTicket === ticket.id && (
                  <div className="px-4 pb-4 border-t border-slate-700/50 pt-4 animate-fadeIn">
                    {/* Original Message */}
                    <div className="mb-4 p-3 bg-slate-900/50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">You • {new Date(ticket.created_at).toLocaleString()}</p>
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">{ticket.message}</p>
                    </div>
                    
                    {/* Conversation Thread */}
                    {ticket.responses && ticket.responses.length > 0 && (
                      <div className="space-y-3 mb-4">
                        {ticket.responses.map((response, idx) => (
                          <div 
                            key={idx} 
                            className={`p-3 rounded-lg ${
                              response.admin_username 
                                ? "bg-cyan-500/10 border border-cyan-500/20" 
                                : "bg-slate-900/50"
                            }`}
                          >
                            <p className={`text-xs mb-1 ${response.admin_username ? "text-cyan-400" : "text-slate-500"}`}>
                              {response.admin_username ? `Admin (${response.admin_username})` : "You"} • {new Date(response.created_at).toLocaleString()}
                            </p>
                            <p className="text-sm text-slate-300 whitespace-pre-wrap">{response.message}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Legacy admin_response support */}
                    {ticket.admin_response && !ticket.responses?.length && (
                      <div className="mb-4 p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
                        <p className="text-xs text-cyan-400 font-medium mb-1">Admin Response:</p>
                        <p className="text-sm text-slate-300">{ticket.admin_response}</p>
                      </div>
                    )}
                    
                    {/* Reply Box (only if ticket is not closed) */}
                    {ticket.status !== "closed" && (
                      <div className="flex gap-2 mt-4">
                        <input
                          type="text"
                          placeholder="Type your reply..."
                          value={expandedTicket === ticket.id ? replyMessage : ""}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          onKeyPress={(e) => e.key === "Enter" && replyToTicket(ticket.id)}
                          className="input-dark flex-1"
                        />
                        <button
                          onClick={() => replyToTicket(ticket.id)}
                          disabled={!replyMessage.trim() || replying}
                          className="btn-primary px-4"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    
                    {ticket.status === "closed" && (
                      <p className="text-center text-slate-500 text-sm mt-4">This ticket has been closed</p>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="card-dark p-6" data-testid="faq-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-cyan-500/10 rounded-lg">
            <HelpCircle className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white font-orbitron">Frequently Asked Questions</h3>
            <p className="text-xs text-slate-500">Quick answers before opening a ticket</p>
          </div>
        </div>
        <div className="space-y-2">
          {faqs.map((faq, index) => {
            const Icon = faq.icon;
            const isOpen = openFaq === index;
            return (
              <div key={index} className="border border-slate-700/50 rounded-xl overflow-hidden transition-colors hover:border-slate-600/50">
                <button
                  onClick={() => setOpenFaq(isOpen ? null : index)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                  data-testid={`faq-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-4 h-4 text-cyan-400 shrink-0" />
                    <span className="text-sm text-white font-medium">{faq.q}</span>
                  </div>
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-slate-500 shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
                  )}
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pt-0">
                    <p className="text-sm text-slate-400 leading-relaxed pl-7">{faq.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Support;
